import { CCStyling } from "../../cclib/CCStyling";
import { CCStructureItem } from "../../cclib/types/structure";

export const instructionsStyling = CCStyling.fromObject({
  "max-width": "1024px"
});

// Used in clinical/structure and assistant when user is CE line
export const generalCEInstructions: CCStructureItem[] = [
  {
    type: "header",
    name: "Overview"
  },
  {
    type: "instructions",
    additionalCss: instructionsStyling,
    text: "The core purpose of annual counseling is to provide candid and helpful feedback and guidance to the individual. The goal is to provide a supportive atmosphere to assist our faculty in their academic careers. However, it should also be recognized that the ultimate responsibility for career trajectory and success lies with each faculty member. Thus, it is up to the faculty member to:"
  },
  {
    type: "instructions",
    additionalCss: instructionsStyling,
    text:
      "<ul>" +
      "<li>Respond to invitations to meet with their department chair and/or service chief</li>" +
      "<li>Request such counseling and mentoring sessions if such sessions are not otherwise scheduled for them</li>" +
      "<li>Attend information sessions offered to them</li>" +
      "<li>Be familiar with policies and procedures concerning reappointment and promotion, particularly those included in the School faculty handbook</li>" +
      "</ul>"
  },
  {
    type: "instructions",
    additionalCss: instructionsStyling,
    text: "Annual Counseling is required for all Clinical Assistant Professors and Clinical Associate Professors with an FTE of 50% or more. Department chairs, division chiefs, or their designates, should confer annually with each junior faculty member in their department to review the faculty member's performance in the light of the criteria for reappointment or promotion. This form serves as documentation for the annual counseling meeting."
  },
  {
    type: "header",
    name: "At this meeting the chair or chief should:"
  },
  {
    type: "instructions",
    additionalCss: instructionsStyling,
    text:
      "<ul>" +
      "<li>Be familiar with the faculty member’s academic history at the school</li>" +
      "<li>Describe the reappointment/promotion process</li>" +
      "<li>State the criteria for the next upcoming action (from School of Medicine Faculty Handbook)</li>" +
      "<li>Complete and sign an annual counseling documentation form explaining what was discussed</li>" +
      "</ul>"
  },
  {
    type: "header",
    name: "The discussion should include topics such as:"
  },
  {
    type: "instructions",
    additionalCss: instructionsStyling,
    text:
      "<ul>" +
      "<li>Criteria and timeline for reappointment or promotion</li>" +
      "<li>Clinical care and clinical teaching/mentorship activities</li>" +
      "<li>Administrative and/or scholarly activities, if applicable</li>" +
      "<li>Quality improvement activities</li>" +
      "<li>Advocacy Activities</li>" +
      "<li>Percent time of appointment and allocation of effort</li>" +
      "<li>Academic/professional support (e.g., availability of mentors, opportunities for learning, resource availability)</li>" +
      "<li>Achievement of career goals</li>" +
      "<li>Professional development opportunities</li>" +
      "<li>Any planned leaves (Stanford employees)</li>" +
      "<li>Financial issues (Stanford employees)</li>" +
      "</ul>"
  }
];
export const preamble = (title: string): CCStructureItem[] => [
  {
    type: "header",
    name: "SCHOOL OF MEDICINE - DEPARTMENT OF EMERGENCY MEDICINE"
  },
  {
    type: "header",
    name: title
  },
  {
    type: "hyperlink",
    text: "Note: Meeting materials may be accessed from a secure shared drive available at this link",
    linkSubstring: {
      start: 31
    },
    linkTo:
      "https://stanfordmedicine.box.com/s/9qmyyxkhi8qv7xmjj4uopwboael94rny",
    htmlAttributes: {
      target: "_blank"
    }
  },
  {
    type: "string",
    key: "faculty_name",
    name: "Faculty Member Name",
    readOnly: true
  },
  {
    type: "string",
    key: "faculty_line",
    name: "Faculty Line",
    readOnly: true
  },
  {
    type: "string",
    key: "faculty_rank",
    name: "Faculty Rank",
    isMentorField: true
  },
  {
    type: "date",
    key: "current_appointment_end_date",
    name: "Appointment End Date",
    isMentorField: true
  },
  {
    type: "date",
    key: "date_of_meeting",
    name: "Date of Meeting",
    isMentorField: true
  },
  {
    type: "string",
    key: "annual_counselor",
    name: "Annual Counselor",
    isMentorField: true
  }
];

const contributionFlexGridRowStyling = CCStyling.fromObject({
  display: "flex",
  "flex-direction": "row",
  gap: "0"
});
const contributionMiddleItem = CCStyling.fromObject({
  display: "block",
  gap: "0",
  "flex-grow": "1",
  "align-content": "center",
  "text-align": "center"
});
const contributionSideItem = CCStyling.fromObject({
  width: "80px",
  display: "inline-block",
  "margin-right": "5px",
  "text-align": "center"
});
export const contributionGrid = (): CCStructureItem[] => [
  {
    type: "grid",
    rows: 0,
    columns: 0,
    htmlAttributes: {
      id: "contrib-container"
    },
    isMentorField: true,
    sections: [
      {
        type: "grid",
        rows: 0,
        columns: 0,
        sections: [
          {
            type: "header",
            name: "Current Proportionality of Contribution (%)",
            additionalCss: CCStyling.fromObject({ display: "block" })
          },
          {
            type: "header",
            name: "Activity",
            additionalCss: CCStyling.fromObject({
              display: "block",
              "align-content": "center",
              "text-align": "center"
            })
          },
          {
            type: "header",
            name: "Anticipated Change, if any (%)",
            additionalCss: CCStyling.fromObject({
              "text-align": "right",
              display: "block"
            })
          }
        ],
        additionalCss: CCStyling.fromObject({
          display: "grid",
          gap: "0",
          "grid-template-columns": "1fr 1fr 1fr"
        })
      },
      {
        type: "grid",
        rows: 0,
        columns: 0,
        sections: [
          {
            type: "integer",
            key: "contrib_clinical",
            additionalCss: contributionSideItem,
            isMentorField: true
          },
          {
            type: "header",
            name: "Clinical Activities",
            additionalCss: contributionMiddleItem
          },
          {
            type: "integer",
            key: "contrib_clinical_change",
            additionalCss: contributionSideItem,
            isMentorField: true
          }
        ],
        additionalCss: contributionFlexGridRowStyling
      },
      {
        type: "grid",
        rows: 0,
        columns: 0,
        sections: [
          {
            type: "integer",
            key: "contrib_teaching",
            additionalCss: contributionSideItem,
            isMentorField: true
          },
          {
            type: "header",
            name: "Teaching Activities",
            additionalCss: contributionMiddleItem
          },
          {
            type: "integer",
            key: "contrib_teaching_change",
            additionalCss: contributionSideItem,
            isMentorField: true
          }
        ],
        additionalCss: contributionFlexGridRowStyling
      },
      {
        type: "grid",
        rows: 0,
        columns: 0,
        sections: [
          {
            type: "integer",
            key: "contrib_admin",
            additionalCss: contributionSideItem,
            isMentorField: true
          },
          {
            type: "header",
            name: "Administrative Activities",
            additionalCss: contributionMiddleItem
          },
          {
            type: "integer",
            key: "contrib_admin_change",
            additionalCss: contributionSideItem,
            isMentorField: true
          }
        ],
        additionalCss: contributionFlexGridRowStyling
      },
      {
        type: "grid",
        rows: 0,
        columns: 0,
        sections: [
          {
            type: "integer",
            key: "contrib_schol",
            additionalCss: contributionSideItem,
            isMentorField: true
          },
          {
            type: "header",
            name: "Scholarship",
            additionalCss: contributionMiddleItem
          },
          {
            type: "integer",
            key: "contrib_schol_change",
            additionalCss: contributionSideItem,
            isMentorField: true
          }
        ],
        additionalCss: contributionFlexGridRowStyling
      },
      {
        type: "grid",
        rows: 0,
        columns: 0,
        sections: [
          {
            type: "integer",
            key: "contrib_other",
            additionalCss: contributionSideItem,
            isMentorField: true
          },
          {
            type: "header",
            name: "Other, if applicable (specify)",
            additionalCss: contributionMiddleItem
          },
          {
            type: "integer",
            key: "contrib_other_change",
            additionalCss: contributionSideItem,
            isMentorField: true
          }
        ],
        additionalCss: contributionFlexGridRowStyling
      }
    ],
    additionalCss: CCStyling.fromObject({
      width: "min(450px, 100%)",
      display: "flex",
      "flex-direction": "column"
    })
  }
];

export const deptEMForm = (includeRepTable: boolean): CCStructureItem[] => [
  {
    type: "header",
    name: "Part 2: Department of EM Form — the information below is for use <em>only within our department.</em>",
    htmlAttributes: {
      id: "part2-boundary"
    }
  },
  {
    type: "paragraph",
    text: "1.\t<strong>What has been the most positive or rewarding experience in your academic career this past year?</strong>",
    key: "positive_experience"
  },
  {
    type: "paragraph",
    text: "2.\t<strong>What have you found most challenging this past year?</strong>",
    key: "challenging_experience"
  },
  {
    type: "paragraph",
    text: "3.\t<strong>The <em>ACTIONABLE</em> and <em>MEASURABLE</em> goals you identified for the upcoming year:</strong>",
    key: "actionable_goal_1"
  },
  {
    type: "paragraph",
    key: "actionable_goal_2"
  },
  {
    type: "paragraph",
    key: "actionable_goal_3"
  },
  {
    type: "paragraph",
    text: "<strong>How can the department best support you in achieving these goals?</strong>",
    key: "department_help_goals"
  },
  ...(includeRepTable
    ? [
        {
          type: "header" as const,
          name: "4.\tContacts who can attest to your professional reputation:"
        },
        {
          type: "custom" as const,
          key: "custom-reputation"
        },
        {
          type: "paragraph" as const,
          text: "<strong>How can the department best support you in enhancing or expanding your professional reputation?</strong>",
          key: "department_help_reputation"
        }
      ]
    : []),
  {
    type: "radio",
    options: ["Yes", "No"],
    text: `<strong>${includeRepTable ? 5 : 4}.\tDo you have questions regarding the bonus/incentive plan?</strong>`,
    key: "questions_bonus_plan"
  },
  {
    type: "paragraph",
    key: "questions_bonus_plan_paragraph",
    optional: true
  },
  ...(includeRepTable
    ? [
        {
          type: "radio" as const,
          options: ["Yes", "No"],
          text: "<strong>6.\tDo you have questions regarding the <u>promotion criteria/process</u>?</strong>",
          key: "questions_promotion"
        },
        {
          type: "paragraph" as const,
          key: "questions_promotion_paragraph",
          optional: true
        }
      ]
    : []),
  {
    type: "paragraph",
    text: "<strong>Narrative Meeting Notes</strong> <em>(internal records only)</em>",
    key: "narrative_meeting_notes"
  },
  ...(includeRepTable
    ? [
        {
          type: "radio" as const,
          options: [
            "Reappointment",
            "Promotion Review will be initiated approximately six months in advance of the current appointment end date",
            "Type of review has not yet been determined"
          ],
          name: "Type of Review",
          key: "type_of_review"
        }
      ]
    : [])
];

export function setIsMentorField(
  item: CCStructureItem,
  isMentorField: boolean
) {
  if (item.type === "grid") {
    item.sections = item.sections.map((gridItem) =>
      setIsMentorField(gridItem, isMentorField)
    );
  }
  return {
    ...item,
    isMentorField: item.type === "header" ? item.isMentorField : isMentorField
  };
}
